// ═══════════════════════════════════════════════════════════
// FOIA — Root Router
// Staff portal: /staff/*  (requires auth)
// Public portal: /portal/* (public-facing)
// ═══════════════════════════════════════════════════════════

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import type { StaffRole } from './store/authStore';
import { JurisdictionProvider } from './contexts/JurisdictionContext';

import Login           from './pages/auth/Login';
import StaffLayout     from './layouts/StaffLayout';
import PortalLayout    from './layouts/PortalLayout';

// Staff pages
import StaffDashboard  from './pages/staff/dashboard/Dashboard';
import RequestList     from './pages/staff/requests/RequestList';
import RequestDetail   from './pages/staff/requests/RequestDetail';
import NewRequest      from './pages/staff/requests/NewRequest';
import Settings        from './pages/staff/settings/Settings';
import Analytics       from './pages/staff/analytics/Analytics';
import RedactionEditor    from './pages/staff/redaction/RedactionEditor';
import CSVRedactionEditor from './pages/staff/redaction/CSVRedactionEditor';
import StandaloneRedact   from './pages/staff/redaction/StandaloneRedact';
import StaffLibrary       from './pages/staff/library/StaffLibrary';

// Public portal pages
import PortalHome      from './pages/portal/Home';
import PortalSubmit    from './pages/portal/Submit';
import PortalTrack     from './pages/portal/Track';
import PortalAccount   from './pages/portal/Account';
import ReleasePage     from './pages/portal/ReleasePage';
import Library         from './pages/portal/Library';
import PaymentPage     from './pages/portal/PaymentPage';

// Public upload page (no auth, no layout wrapper)
import UploadPage      from './pages/upload/UploadPage';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(s => s.user);
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireRole({ roles, children }: { roles: StaffRole[]; children: React.ReactNode }) {
  const user = useAuthStore(s => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.staffRole && !roles.includes(user.staffRole)) {
    return (
      <div style={{ padding: '60px 32px', textAlign: 'center', color: 'var(--color-neutral-500)' }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--color-neutral-800)' }}>Access Restricted</div>
        <div style={{ fontSize: 13 }}>You don't have permission to view this page.</div>
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<Login />} />

        {/* Staff portal (authenticated) */}
        <Route path="/staff" element={<RequireAuth><StaffLayout /></RequireAuth>}>
          <Route index element={<StaffDashboard />} />
          <Route path="requests" element={<RequestList />} />
          <Route path="requests/new" element={<RequireRole roles={['admin', 'coordinator']}><NewRequest /></RequireRole>} />
          <Route path="requests/:id" element={<RequestDetail />} />
          <Route path="analytics" element={<RequireRole roles={['admin', 'coordinator', 'supervisor']}><Analytics /></RequireRole>} />
          <Route path="library" element={<StaffLibrary />} />
          <Route path="redact"            element={<StandaloneRedact />} />
          <Route path="redact/:docId"     element={<RedactionEditor />} />
          <Route path="redact-csv/:docId" element={<CSVRedactionEditor />} />
          <Route path="settings" element={<RequireRole roles={['admin']}><Settings /></RequireRole>} />
        </Route>

        {/* Public portal */}
        <Route path="/portal" element={<JurisdictionProvider><PortalLayout /></JurisdictionProvider>}>
          <Route index element={<PortalHome />} />
          <Route path="submit" element={<PortalSubmit />} />
          <Route path="track" element={<PortalTrack />} />
          <Route path="track/:requestNumber" element={<PortalTrack />} />
          <Route path="account" element={<PortalAccount />} />
          <Route path="release/:token" element={<ReleasePage />} />
          <Route path="library" element={<Library />} />
          <Route path="pay/:token" element={<PaymentPage />} />
        </Route>

        {/* Public upload page — no auth, no layout */}
        <Route path="/upload/:token" element={<UploadPage />} />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/staff" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
